<template>
  <div
    class="relative bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out"
    :style="{ width: isCollapsed ? '0' : '29.5rem' }"
  >
    <!-- Chevron de rétraction -->
    <div class="absolute -right-6 top-[200px] z-30">
      <button
        class="bg-white border border-gray-200 w-6 h-12 flex items-center justify-center shadow-sm"
        @click="toggleCollapse"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 text-gray-600 transition-transform duration-300"
          :class="{ 'rotate-180': isCollapsed }"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M15 19l-7-7 7-7"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <div
      class="flex-1 flex flex-col min-h-0"
      :class="{ 'opacity-0': isCollapsed }"
    >
      <!-- Onglets -->
      <div class="border-b border-gray-200 flex-shrink-0 products-panel">
        <nav class="flex">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            :class="[
              'flex-1 py-3 px-4 text-sm font-medium text-center',
              activeTab === tab.id
                ? 'text-brand border-b-2 border-brand'
                : 'text-gray-500 hover:text-gray-700',
            ]"
            @click="activeTab = tab.id"
          >
            {{ tab.name }}
          </button>
        </nav>
      </div>

      <!-- Contenu des onglets -->
      <div class="flex-1 overflow-y-auto min-h-0">
        <!-- Onglet Produits -->
        <div v-if="activeTab === 'products'" class="p-4">
          <!-- Barre de recherche -->
          <div class="mb-4">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Rechercher un produit"
              class="w-full px-4 py-2 border border-gray-300 rounded-md"
            >

            <!-- Filtres avancés -->
            <div
              v-if="searchQuery"
              class="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200"
            >
              <div class="text-sm font-medium text-gray-700 mb-2">
                Filtres avancés
              </div>

              <!-- Filtre par fournisseur -->
              <div class="mb-2">
                <label class="block text-xs text-gray-600 mb-1">Fournisseur</label>
                <select
                  v-model="selectedSupplier"
                  class="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">
                    Tous les fournisseurs
                  </option>
                  <option
                    v-for="supplier in filteredSuppliers"
                    :key="supplier.name"
                    :value="supplier.name"
                  >
                    {{ supplier.name }}
                  </option>
                </select>
              </div>

              <!-- Filtre par gamme de prix -->
              <div class="mb-2">
                <label class="block text-xs text-gray-600 mb-1">Gamme de prix</label>
                <div class="flex items-center gap-2">
                  <input
                    v-model="priceRange.min"
                    type="number"
                    placeholder="Min"
                    class="w-1/2 px-2 py-1 text-sm border border-gray-300 rounded-md"
                  >
                  <span class="text-gray-500">-</span>
                  <input
                    v-model="priceRange.max"
                    type="number"
                    placeholder="Max"
                    class="w-1/2 px-2 py-1 text-sm border border-gray-300 rounded-md"
                  >
                </div>
              </div>

              <!-- Boutons de filtres -->
              <div class="flex justify-end gap-2 mt-3">
                <button
                  class="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
                  @click="resetFilters"
                >
                  Réinitialiser
                </button>
                <button
                  class="px-3 py-1 text-xs bg-brand text-white rounded-md hover:bg-[#8c0013]"
                  @click="applyFilters"
                >
                  Appliquer
                </button>
              </div>
            </div>
          </div>

          <!-- Navigation des fournisseurs -->
          <div class="mb-4">
            <select
              v-model="selectedSupplier"
              class="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">
                Tous les fournisseurs
              </option>
              <option
                v-for="supplier in filteredSuppliers"
                :key="supplier.name"
                :value="supplier.name"
              >
                {{ supplier.name }}
              </option>
            </select>
          </div>

          <!-- Liste des produits -->
          <div class="grid grid-cols-2 gap-4">
            <div
              v-for="product in filteredProducts"
              :key="product.id"
              class="product-item bg-white border border-gray-200 rounded-md p-2 cursor-pointer hover:border-brand transition-colors duration-200"
              draggable="true"
              @dragstart="onDragStart($event, getFormattedProduct(product))"
            >
              <div
                class="aspect-square bg-gray-50 rounded-md overflow-hidden mb-2"
              >
                <img
                  :src="getImageUrl(product.image_path)"
                  :alt="product.product?.name || 'Produit'"
                  class="w-full h-full object-contain p-1"
                  @error="handleImageError"
                >
              </div>
              <div class="text-xs">
                <h3 class="font-medium truncate">
                  {{ product.product?.name || "Sans nom" }}
                </h3>
                <p class="text-gray-500 text-xs truncate">
                  Réf: {{ product.product?.reference || "N/A" }}
                </p>
                <p class="text-brand font-bold mt-1">
                  {{
                    formatPrice(
                      parseFloat(
                        String(product.product?.base_price || "0").replace(
                          ",",
                          "."
                        )
                      )
                    )
                  }}
                </p>
              </div>
            </div>
          </div>

          <!-- Message si aucun produit -->
          <div
            v-if="filteredProducts.length === 0"
            class="text-center text-gray-500 mt-4"
          >
            Aucun produit trouvé
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="mt-4 pt-4 border-t border-gray-200">
            <div class="flex items-center justify-between">
              <button
                :disabled="currentPage === 1"
                class="p-2 text-gray-500 hover:text-brand disabled:opacity-50 disabled:cursor-not-allowed"
                @click="changePage(currentPage - 1)"
              >
                &lt;
              </button>
              <div class="flex items-center gap-2">
                <span class="text-sm">{{ currentPage }}</span>
                <span class="text-sm text-gray-500">/</span>
                <span class="text-sm text-gray-500">{{ totalPages }}</span>
              </div>
              <button
                :disabled="currentPage === totalPages"
                class="p-2 text-gray-500 hover:text-brand disabled:opacity-50 disabled:cursor-not-allowed"
                @click="changePage(currentPage + 1)"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>

        <!-- Onglet Marques -->
        <div v-else-if="activeTab === 'brands'" class="p-4">
          <div
            v-if="brands && brands.length > 0"
            class="grid grid-cols-2 gap-4"
          >
            <div
              v-for="brand in brands"
              :key="brand?.id || ''"
              class="brand-item p-4 border rounded-lg hover:shadow-md transition-shadow"
              draggable="true"
              @dragstart="onDragStart($event, brand)"
            >
              <img
                v-if="brand?.image_name"
                :src="getBrandImageUrl(brand.image_name)"
                :alt="brand?.name || ''"
                class="w-full h-24 object-contain mb-2"
                @error="handleImageError"
              >
              <h3 class="text-sm font-medium text-center">
                {{ brand?.name || "" }}
              </h3>
            </div>
          </div>
          <div v-else class="text-center text-gray-500 mt-4">
            Aucune marque disponible
          </div>
        </div>

        <!-- Onglet Pictogrammes -->
        <div v-else-if="activeTab === 'pictos'" class="p-4">
          <div class="grid grid-cols-3 gap-4">
            <div
              v-for="picto in pictograms"
              :key="picto.id"
              class="picto-item p-4 border rounded-lg hover:shadow-md transition-shadow"
              draggable="true"
              @dragstart="onDragStart($event, picto)"
            >
              <img
                :src="getPictoImageUrl(picto.image_name)"
                :alt="picto.name"
                class="w-full h-16 object-contain mb-2"
                @error="handleImageError"
              >
              <p class="text-xs text-center">
                {{ picto.name }}
              </p>
            </div>
          </div>
        </div>

        <!-- Onglet Bannières -->
        <div v-else-if="activeTab === 'banners'" class="p-4">
          <div class="space-y-4">
            <div
              v-for="banner in banners"
              :key="banner.id"
              class="banner-item p-4 border rounded-lg hover:shadow-md transition-shadow"
              draggable="true"
              @dragstart="onDragStart($event, banner)"
            >
              <img
                :src="getHeaderImageUrl(banner.image_name)"
                :alt="banner.name"
                class="w-full h-32 object-cover mb-2 rounded"
                @error="handleImageError"
              >
              <h3 class="text-sm font-medium">
                {{ banner.name }}
              </h3>
              <span v-if="banner.featured" class="text-xs text-brand">
                Bannière mise en avant
              </span>
            </div>
          </div>
        </div>

        <!-- Onglet Mes Produits -->
        <div v-else-if="activeTab === 'my-products'" class="p-4">
          <!-- Barre de recherche -->
          <div class="mb-4">
            <input
              v-model="myProductsSearchQuery"
              type="text"
              placeholder="Rechercher dans mes produits"
              class="w-full px-4 py-2 border border-gray-300 rounded-md"
            >
          </div>

          <!-- Liste des produits -->
          <div class="grid grid-cols-2 gap-4">
            <div
              v-for="product in filteredMyProducts"
              :key="product.id"
              class="product-item bg-white border border-gray-200 rounded-md p-2 cursor-pointer hover:border-brand transition-colors duration-200"
              draggable="true"
              @dragstart="onDragStart($event, formatUserProduct(product))"
            >
              <div
                class="aspect-square bg-gray-50 rounded-md overflow-hidden mb-2"
              >
                <img
                  v-if="
                    product.product_images && product.product_images.length > 0
                  "
                  :src="getImageUrl(product.product_images[0].image_path)"
                  :alt="product.name || 'Produit'"
                  class="w-full h-full object-contain p-1"
                  @error="handleImageError"
                >
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center text-gray-400"
                >
                  Pas d'image
                </div>
              </div>
              <div class="text-xs">
                <h3 class="font-medium truncate">
                  {{ product.name || "Sans nom" }}
                </h3>
                <p class="text-gray-500 text-xs truncate">
                  Réf: {{ product.reference || "N/A" }}
                </p>
                <p
                  v-if="product.base_price"
                  class="text-brand font-bold mt-1"
                >
                  {{
                    formatPrice(
                      parseFloat(String(product.base_price).replace(",", "."))
                    )
                  }}
                </p>
                <p v-else class="text-gray-500 text-xs mt-1">
                  Prix non défini
                </p>
              </div>
            </div>
          </div>

          <!-- Message si aucun produit -->
          <div
            v-if="filteredMyProducts.length === 0"
            class="text-center text-gray-500 mt-4"
          >
            Aucun produit trouvé
          </div>

          <!-- Pagination -->
          <div
            v-if="totalMyProductsPages > 1"
            class="mt-4 pt-4 border-t border-gray-200"
          >
            <div class="flex items-center justify-between">
              <button
                :disabled="currentMyProductsPage === 1"
                class="p-2 text-gray-500 hover:text-brand disabled:opacity-50 disabled:cursor-not-allowed"
                @click="changeMyProductsPage(currentMyProductsPage - 1)"
              >
                &lt;
              </button>
              <div class="flex items-center gap-2">
                <span class="text-sm">{{ currentMyProductsPage }}</span>
                <span class="text-sm text-gray-500">/</span>
                <span class="text-sm text-gray-500">{{
                  totalMyProductsPages
                }}</span>
              </div>
              <button
                :disabled="currentMyProductsPage === totalMyProductsPages"
                class="p-2 text-gray-500 hover:text-brand disabled:opacity-50 disabled:cursor-not-allowed"
                @click="changeMyProductsPage(currentMyProductsPage + 1)"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import api from "@/api/api";
import { computed, onMounted, ref } from "@vue/runtime-core";
import { useCurrentBrand, useCurrentUser } from "@/utils/storage";

const apiUrl = import.meta.env.VITE_API_URL;

// Types
interface ProductImage {
  id: number;
  image_name: string;
  image_path: string;
  product: {
    id: number;
    name: string;
    description: string;
    base_price: string | null;
    reference: string;
    category_id: number | null;
    user_id: number;
    user: {
      id: number;
      roles?: string[];
      user_details: {
        id: number;
        name: string;
        company_name?: string;
      };
    };
  };
}

interface UserProduct {
  id: number;
  user_id: number;
  name: string;
  reference: string;
  description: string;
  base_price: string | null;
  category_id: number | null;
  group: string | null;
  deleted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  product_images: Array<{
    id: number;
    product_id: number;
    image_name: string;
    image_path: string;
    updated_at: string;
    created_at: string | null;
    deleted_at: string | null;
  }>;
  groups: Array<any>;
}

interface FormattedProduct {
  id: number;
  name: string;
  description: string;
  price: number;
  image: string;
  reference: string;
  image_name: string;
  base_price: string;
  category_id: number | null;
  product_image_id: number;
  product_image?: {
    image_name: string;
  };
}

interface Brand {
  id: number;
  isActive: boolean;
  name: string;
  imageName: string;
  groups: Array<{
    id: number;
    name: string;
    slug: string;
    brand: string;
  }>;
}

interface Pictogram {
  id: number;
  isActive: boolean;
  name: string;
  imageName: string;
  groups: Array<{
    id: number;
    name: string;
    slug: string;
    brand: string;
  }>;
}

interface Banner {
  id: number;
  isActive: boolean;
  featured: boolean;
  name: string;
  imageName: string;
  groups: Array<{
    id: number;
    name: string;
    slug: string;
    brand: string;
  }>;
}

// Configuration des onglets
const tabs = [
  { id: "products", name: "Produits" },
  { id: "brands", name: "Marques" },
  { id: "pictos", name: "Pictos" },
  { id: "banners", name: "Bannières" },
  { id: "my-products", name: "Mes Produits" },
] as const;

type TabId = (typeof tabs)[number]["id"];

// État local
const activeTab = ref<TabId>("products");
const searchQuery = ref("");
const selectedSupplier = ref("");
const currentPage = ref(1);
const itemsPerPage = 12;
const products = ref<ProductImage[]>([]);
const banners = ref<Banner[]>([]);
const brands = ref<Brand[]>([]);
const pictograms = ref<Pictogram[]>([]);
const isCollapsed = ref(false);

// État local pour Mes Produits
const myProducts = ref<UserProduct[]>([]);
const myProductsSearchQuery = ref("");
const currentMyProductsPage = ref(1);
const myProductsPerPage = 12;

// État local pour les filtres
const priceRange = ref({ min: null, max: null });
const appliedFilters = ref({
  supplier: "",
  priceRange: { min: null, max: null },
});

// Méthodes
const getImageUrl = (imagePath: string): string => {
  return `${apiUrl}/upl/${imagePath}`;
};

const getBrandImageUrl = (imageName: string): string => {
  return `${apiUrl}/upl/brands/${imageName}`;
};

const getPictoImageUrl = (imageName: string): string => {
  return `${apiUrl}/upl/pictos/${imageName}`;
};

const getHeaderImageUrl = (imageName: string): string => {
  return `${apiUrl}/upl/headers/${imageName}`;
};

const getFormattedProduct = (
  productImage: ProductImage | null
): FormattedProduct | null => {
  if (!productImage?.product) {
    console.warn("Product data is missing:", productImage);
    return null;
  }

  const price =
    typeof productImage.product.base_price === "string"
      ? parseFloat(productImage.product.base_price.replace(",", "."))
      : typeof productImage.product.base_price === "number"
      ? productImage.product.base_price
      : 0;

  return {
    id: productImage.product.id,
    name: productImage.product.name || "Sans nom",
    description:
      productImage.product.description ||
      productImage.product.name ||
      "Sans description",
    price: parseFloat(
      String(productImage.product.base_price || "0").replace(",", ".")
    ),
    image: productImage.image_path ? getImageUrl(productImage.image_path) : "",
    reference: productImage.product.reference || "",
    image_name: productImage.image_name || "",
    base_price: price.toString(),
    category_id: productImage.product.category_id,
    product_image_id: productImage.id,
  };
};

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(price);
};

// Computed
const uniqueSuppliers = computed(() => {
  const uniqueSuppliers = new Map<string, { ids: number[]; name: string }>();

  products.value.forEach((product) => {
    if (
      product.product?.user?.user_details &&
      product.product.user.roles?.includes("ROLE_SUPPLIER")
    ) {
      const supplier = product.product.user.user_details;
      const supplierName = supplier.name.toUpperCase().trim();

      if (!uniqueSuppliers.has(supplierName)) {
        uniqueSuppliers.set(supplierName, {
          ids: [supplier.id],
          name: supplier.name,
        });
      } else {
        const existingSupplier = uniqueSuppliers.get(supplierName);
        if (existingSupplier && !existingSupplier.ids.includes(supplier.id)) {
          existingSupplier.ids.push(supplier.id);
        }
      }
    }
  });

  return Array.from(uniqueSuppliers.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const filteredSuppliers = computed(() => {
  if (!searchQuery.value) {
    return uniqueSuppliers.value;
  }

  const filteredProducts = products.value.filter(
    (p): p is ProductImage =>
      Boolean(p?.product) &&
      (p.product.name
        ?.toLowerCase()
        .includes(searchQuery.value.toLowerCase()) ||
        p.product.reference
          ?.toLowerCase()
          .includes(searchQuery.value.toLowerCase()) ||
        p.product.user?.user_details?.name
          ?.toLowerCase()
          .includes(searchQuery.value.toLowerCase()) ||
        searchQuery.value
          .toLowerCase()
          .split(" ")
          .every((term) => {
            const productName = p.product.name?.toLowerCase() || "";
            const supplierName =
              p.product.user?.user_details?.name?.toLowerCase() || "";
            return productName.includes(term) || supplierName.includes(term);
          }))
  );

  const suppliersMap = new Map<string, { ids: number[]; name: string }>();

  filteredProducts.forEach((product) => {
    if (
      product.product?.user?.user_details &&
      product.product.user.roles?.includes("ROLE_SUPPLIER")
    ) {
      const supplier = product.product.user.user_details;
      const supplierName = supplier.name.toUpperCase().trim();

      if (!suppliersMap.has(supplierName)) {
        suppliersMap.set(supplierName, {
          ids: [supplier.id],
          name: supplier.name,
        });
      } else {
        const existingSupplier = suppliersMap.get(supplierName);
        if (existingSupplier && !existingSupplier.ids.includes(supplier.id)) {
          existingSupplier.ids.push(supplier.id);
        }
      }
    }
  });

  return Array.from(suppliersMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const filteredBySearch = computed(() => {
  let filtered = products.value.filter((p): p is ProductImage =>
    Boolean(p?.product)
  );

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      (p) =>
        p.product.name?.toLowerCase().includes(query) ||
        p.product.reference?.toLowerCase().includes(query) ||
        p.product.user?.user_details?.name?.toLowerCase().includes(query) ||
        query.split(" ").every((term) => {
          const productName = p.product.name?.toLowerCase() || "";
          const supplierName =
            p.product.user?.user_details?.name?.toLowerCase() || "";
          return productName.includes(term) || supplierName.includes(term);
        })
    );
  }

  // Filtrage par fournisseur sélectionné (fonctionne indépendamment des filtres avancés)
  if (selectedSupplier.value) {
    const selectedSupplierIds =
      uniqueSuppliers.value.find(
        (supplier) => supplier.name === selectedSupplier.value
      )?.ids || [];

    filtered = filtered.filter(
      (p) =>
        p.product?.user?.user_details &&
        selectedSupplierIds.includes(p.product.user.user_details.id)
    );
  }

  // Filtrage par les filtres avancés appliqués
  if (appliedFilters.value.supplier) {
    const selectedSupplierIds =
      filteredSuppliers.value.find(
        (supplier) => supplier.name === appliedFilters.value.supplier
      )?.ids || [];

    filtered = filtered.filter(
      (p) =>
        p.product?.user?.user_details &&
        selectedSupplierIds.includes(p.product.user.user_details.id)
    );
  }

  if (appliedFilters.value.priceRange.min !== null) {
    filtered = filtered.filter(
      (p) =>
        parseFloat(p.product.base_price || "0") >=
        (appliedFilters.value.priceRange.min || 0)
    );
  }

  if (appliedFilters.value.priceRange.max !== null) {
    filtered = filtered.filter(
      (p) =>
        parseFloat(p.product.base_price || "0") <=
        (appliedFilters.value.priceRange.max || Infinity)
    );
  }

  return filtered;
});

const filteredProducts = computed(() => {
  let filtered = filteredBySearch.value;

  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filtered.slice(start, end);
});

const totalPages = computed(() =>
  Math.ceil(filteredBySearch.value.length / itemsPerPage)
);

const onDragStart = (
  event: DragEvent,
  item: FormattedProduct | Brand | Pictogram | Banner
): void => {
  if (!item || !event.dataTransfer) return;

  event.dataTransfer.effectAllowed = "copy";
  event.dataTransfer.dropEffect = "copy";

  const itemType =
    activeTab.value === "products" || activeTab.value === "my-products"
      ? "product"
      : activeTab.value === "brands"
      ? "brand"
      : activeTab.value === "pictos"
      ? "pictogram"
      : "banner";

  const dataToSend =
    itemType === "product"
      ? item
      : {
          id: item.id,
          name: item.name,
          image_name: "imageName" in item ? item.imageName : item.image_name,
        };

  const transferData = {
    type: itemType,
    data: dataToSend,
  };

  event.dataTransfer.setData("text/plain", JSON.stringify(transferData));
};

const changePage = (page: number): void => {
  currentPage.value = page;
};

const toggleCollapse = (): void => {
  isCollapsed.value = !isCollapsed.value;
};

const { brand: reactiveBrand } = useCurrentBrand();
const brand = computed(() => {
  return reactiveBrand.value || "hv";
});

// Computed pour Mes Produits
const filteredMyProductsBySearch = computed(() => {
  if (!myProductsSearchQuery.value) {
    return myProducts.value;
  }
  const searchQuery = myProductsSearchQuery.value.toLowerCase();
  return myProducts.value.filter(
    (product: UserProduct) =>
      product.name?.toLowerCase().includes(searchQuery) ||
      product.reference?.toLowerCase().includes(searchQuery)
  );
});

const filteredMyProducts = computed(() => {
  const start = (currentMyProductsPage.value - 1) * myProductsPerPage;
  const end = start + myProductsPerPage;
  return filteredMyProductsBySearch.value.slice(start, end);
});

const totalMyProductsPages = computed(() =>
  Math.ceil(filteredMyProductsBySearch.value.length / myProductsPerPage)
);

const changeMyProductsPage = (page: number): void => {
  currentMyProductsPage.value = page;
};

const { userData } = useCurrentUser();
const user = computed(() => {
  return userData.value;
});

const formatUserProduct = (product: UserProduct): FormattedProduct => {
  const firstImage =
    product.product_images && product.product_images.length > 0
      ? product.product_images[0]
      : null;

  return {
    id: product.id,
    name: product.name || "Sans nom",
    description: product.description || product.name || "Sans description",
    price: product.base_price
      ? parseFloat(String(product.base_price).replace(",", "."))
      : 0,
    image: firstImage ? `${apiUrl}/upl/products/${firstImage.image_name}` : "",
    reference: product.reference || "",
    image_name: firstImage ? firstImage.image_name : "",
    base_price: product.base_price || "0",
    category_id:
      typeof product.category_id === "number" ? product.category_id : 0,
    product_image_id: firstImage ? firstImage.id : 0,
    product_image: firstImage
      ? {
          image_name: firstImage.image_name,
        }
      : undefined,
  };
};

// Méthodes pour les filtres
const resetFilters = () => {
  selectedSupplier.value = "";
  priceRange.value = { min: null, max: null };
  appliedFilters.value = {
    supplier: "",
    priceRange: { min: null, max: null },
  };
};

const applyFilters = () => {
  appliedFilters.value = {
    supplier: selectedSupplier.value,
    priceRange: { ...priceRange.value },
  };
};

onMounted(async () => {
  try {
    // Vérifier que l'utilisateur est connecté
    if (!user.value?.id) {
      console.warn('⚠️  Utilisateur non connecté, impossible de charger les produits');
      return;
    }

    const [
      productsResponse,
      bannersResponse,
      brandsResponse,
      pictogramsResponse,
      myProductsResponse,
    ] = await Promise.all([
      api.get("/product_images?populate=product.user.user_details"),
      api.get(`/support_headers?groups.slug=${brand.value}&isActive=1`),
      api.get(`/brands?itemsPerPage=100&groups.slug=${brand.value}`),
      api.get(`/pictograms?groups.slug=${brand.value}`),
      api.get(`/product_images/product/${user.value.id}`),
    ]);

    products.value = Array.isArray(productsResponse.data)
      ? productsResponse.data
      : Array.isArray(productsResponse.data?.data)
      ? productsResponse.data.data
      : [];

    banners.value = Array.isArray(bannersResponse.data)
      ? bannersResponse.data
      : Array.isArray(bannersResponse.data?.data)
      ? bannersResponse.data.data
      : [];

    const brandsData = Array.isArray(brandsResponse.data)
      ? brandsResponse.data
      : Array.isArray(brandsResponse.data?.data)
      ? brandsResponse.data.data
      : [];
    brands.value = brandsData.sort((a: any, b: any) => (a?.name || '').localeCompare(b?.name || '', 'fr'));

    pictograms.value = Array.isArray(pictogramsResponse.data)
      ? pictogramsResponse.data
      : Array.isArray(pictogramsResponse.data?.data)
      ? pictogramsResponse.data.data
      : [];

    myProducts.value = Array.isArray(myProductsResponse.data)
      ? myProductsResponse.data
      : Array.isArray(myProductsResponse.data?.data)
      ? myProductsResponse.data.data
      : [];
  } catch (error) {
    console.error("Erreur lors du chargement des données:", error);
    if (error instanceof Error) {
      console.error("Message d'erreur:", error.message);
    }
  }
});
</script>

<script lang="ts">
export default {
  name: "ProductsPanel",
};
</script>

<style scoped>
.products-panel {
  height: fit-content;
  max-height: calc(100vh - 80px);
}
</style>
